import {
    ChartsFilters,
    ChartsQueryResponse,
    ChartsTemplatesQueryResponse,
    useGetLibraryCharts,
    useGetLibraryChartsTemplates,
} from '../hooks/getCharts';
import {
    INDICATOR_CARD_WIDTH,
    SPACE_BETWEEN_INDICATORS,
} from '../helpers/constants';

import Button from '@mui/material/Button';
import IndicatorCard from './IndicatorCard';
import { IndicatorFilterT } from '../helpers/types';
import { NavigationDirection } from './types';
import React from 'react';
import ScrollButton from './ui/ScrollButton';
import SeeMoreModal from './ui/SeeMoreModal';
import Typography from '@mui/material/Typography';
import { UseQueryResult } from '@tanstack/react-query';
import { VizType } from 'business/definitions';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

const FIRST_PAGE = 1;

const StyIndicatorsBanners = styled('div')(({ theme }) => ({
    background: theme.palette.lightGrey,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    width: '100%',
}));

const HeaderContainer = styled('div')({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
});

const IndicatorsContainer = styled('div')({
    display: 'flex',
    gap: SPACE_BETWEEN_INDICATORS,
    overflowX: 'auto',
    width: '100%',
});

const StyTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'underline',
}));

type IndicatorsBannersProps = {
    aggregationsFilter: IndicatorFilterT<Aggregation>[];
    integrationsFilter: IndicatorFilterT<string>[];
    metricsFilter: string[];
    search: string;
    vizTypesFilter: IndicatorFilterT<VizType>[];
};

type SingleBannerProps = IndicatorsBannersProps & {
    useGetCharts: (
        chartsFilters: ChartsFilters,
        page: number,
    ) => UseQueryResult<ChartsQueryResponse | ChartsTemplatesQueryResponse>;
    isModel?: boolean;
};

const SingleBanner = ({
    aggregationsFilter,
    integrationsFilter,
    metricsFilter,
    search,
    vizTypesFilter,
    useGetCharts,
    isModel = false,
}: SingleBannerProps): JSX.Element => {
    const { t } = useTranslation();
    const navRef = React.useRef<HTMLDivElement>(null);
    const [canScrollToLeft, setCanScrollToLeft] = React.useState(false);
    const [canScrollToRight, setCanScrollToRight] = React.useState(false);
    const [showMore, setShowMore] = React.useState(false);

    const chartsFilters = {
        aggregations: aggregationsFilter,
        integrations: integrationsFilter,
        metrics: metricsFilter,
        title: search || undefined,
        vizTypes: vizTypesFilter,
    };
    const { data } = useGetCharts(chartsFilters, FIRST_PAGE);

    React.useEffect(() => {
        const container = navRef.current;
        if (container) {
            const handleScroll = () => {
                setCanScrollToLeft(container.scrollLeft > 0);
                setCanScrollToRight(
                    container.scrollWidth >
                        container.clientWidth + container.scrollLeft,
                );
            };

            handleScroll();

            container.addEventListener('scroll', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        } else {
            return undefined;
        }
    }, [data]);

    if (!data) return <></>;

    const { count, charts: indicators } = data;

    const handleNavigation = (direction: NavigationDirection) => {
        if (!navRef || !navRef.current) return;
        const scrollOffset = INDICATOR_CARD_WIDTH + SPACE_BETWEEN_INDICATORS;
        if (direction === 'left') {
            navRef.current.scrollLeft -= scrollOffset;
        } else {
            navRef.current.scrollLeft += scrollOffset;
        }
    };

    const bannerTitle = `${
        isModel
            ? t('indicators.modelIndicators')
            : t('indicators.cityIndicators')
    } (${count})`;

    const bannerId = `indicators-banner-${isModel ? 'model' : 'city'}`;

    return (
        <StyIndicatorsBanners id={bannerId}>
            <HeaderContainer>
                <Typography variant="h5">{bannerTitle}</Typography>
                <Button
                    onClick={() => setShowMore(true)}
                    id="indicators-see-more"
                >
                    <StyTypography variant="body1Bold">
                        {t('common.seeMore')}
                    </StyTypography>
                </Button>
                {showMore && (
                    <SeeMoreModal
                        aggregationsFilter={aggregationsFilter}
                        integrationsFilter={integrationsFilter}
                        isModel={isModel}
                        isOpen={showMore}
                        metricsFilter={metricsFilter}
                        setIsOpen={setShowMore}
                        search={search}
                        title={bannerTitle}
                        useGetCharts={useGetCharts}
                        vizTypesFilter={vizTypesFilter}
                    />
                )}
            </HeaderContainer>
            {count > 0 && (
                <div style={{ position: 'relative' }}>
                    {canScrollToLeft && (
                        <ScrollButton
                            direction="left"
                            handleNavigation={handleNavigation}
                        />
                    )}
                    <IndicatorsContainer ref={navRef}>
                        {indicators.map((indicator) => (
                            <IndicatorCard
                                key={indicator.key}
                                indicator={indicator}
                                isModel={isModel}
                            />
                        ))}
                    </IndicatorsContainer>
                    {canScrollToRight && (
                        <ScrollButton
                            direction="right"
                            handleNavigation={handleNavigation}
                            onClick={() => setCanScrollToLeft(true)}
                        />
                    )}
                </div>
            )}
        </StyIndicatorsBanners>
    );
};

const IndicatorsBanners = ({
    aggregationsFilter,
    integrationsFilter,
    metricsFilter,
    search,
    vizTypesFilter,
}: IndicatorsBannersProps): JSX.Element => (
    <>
        <SingleBanner
            aggregationsFilter={aggregationsFilter}
            integrationsFilter={integrationsFilter}
            metricsFilter={metricsFilter}
            search={search}
            vizTypesFilter={vizTypesFilter}
            useGetCharts={useGetLibraryChartsTemplates}
            isModel
        />
        <SingleBanner
            aggregationsFilter={aggregationsFilter}
            integrationsFilter={integrationsFilter}
            metricsFilter={metricsFilter}
            search={search}
            vizTypesFilter={vizTypesFilter}
            useGetCharts={useGetLibraryCharts}
        />
    </>
);

export default IndicatorsBanners;
